import { ArrowLeft, Check, Loader2, Pause, Play, Zap } from 'lucide-react';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/cn';
import { AUTOMATION_STATUS, type EmailAutomation } from '../../../types/automazioni.types';
import { ProblemiSequenza } from './ProblemiSequenza';

interface TestataSequenzaProps {
  automazione: EmailAutomation;
  /** Da dove entrano i contatti, gia' detto a parole. */
  partenza: string;
  problemi: string[] | null;
  salvando: boolean;
  onIndietro: () => void;
  onRinomina: (nome: string) => void;
  onNomeConfermato: () => void;
  onAccendi: () => void;
  onPausa: () => void;
}

/** La riga in cima all'editor: nome, stato, cosa manca e il tasto per accendere. */
export function TestataSequenza({
  automazione,
  partenza,
  problemi,
  salvando,
  onIndietro,
  onRinomina,
  onNomeConfermato,
  onAccendi,
  onPausa,
}: TestataSequenzaProps) {
  const stato = AUTOMATION_STATUS[automazione.status];
  const attiva = automazione.status === 'attiva';

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 pb-3">
      <button
        type="button"
        onClick={onIndietro}
        aria-label="Torna alle sequenze"
        className="w-9 h-9 flex items-center justify-center text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
      </button>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <input
            value={automazione.name}
            onChange={(e) => onRinomina(e.target.value)}
            onBlur={onNomeConfermato}
            aria-label="Nome della sequenza"
            className="min-w-0 max-w-full text-lg font-semibold text-neutral-900 bg-transparent px-1.5 -mx-1.5 py-0.5 hover:bg-neutral-100 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 transition-colors"
            style={{ width: `${Math.max(automazione.name.length, 4) + 1}ch` }}
          />
          <span
            className={cn(
              'inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 border font-medium',
              stato.badge
            )}
          >
            <span className={cn('w-1.5 h-1.5 rounded-full', stato.accento)} />
            {stato.label}
          </span>
          {automazione.servizio && (
            <span
              title="Informazioni sul corso: arriva anche a chi non ha accettato le promozioni"
              className="inline-flex items-center text-[11px] px-2 py-0.5 border font-medium bg-primary-50 text-primary-700 border-primary-200"
            >
              Info corso
            </span>
          )}
        </div>
        <p className="flex items-center gap-1.5 text-xs text-neutral-500 mt-0.5">
          <Zap className="w-3 h-3 text-neutral-400" />
          <span className="truncate">{partenza}</span>
          <span className="text-neutral-300">·</span>
          <span className="whitespace-nowrap">{automazione.entrati_count} entrati</span>
          <span className="text-neutral-300">·</span>
          <span className="whitespace-nowrap">{automazione.finiti_count} arrivati in fondo</span>
        </p>
      </div>

      {!attiva && (
        <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-neutral-400 min-w-[92px]">
          {salvando ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" />
              Salvo...
            </>
          ) : (
            <>
              <Check className="w-3 h-3" />
              Salvata
            </>
          )}
        </span>
      )}

      {!attiva && <ProblemiSequenza problemi={problemi} />}

      {attiva ? (
        <Button variant="outline" onClick={onPausa} className="rounded-none">
          <Pause className="w-4 h-4 mr-2" />
          Metti in pausa
        </Button>
      ) : (
        <Button
          onClick={onAccendi}
          className="rounded-none bg-primary-600 hover:bg-primary-700 text-white shadow-[inset_0_-2px_0_rgba(0,0,0,0.15)]"
        >
          <Play className="w-4 h-4 mr-2" />
          Accendi
        </Button>
      )}
    </div>
  );
}
